/**
 * tareasDeLaMinuta — qué zona del acta son las tareas y qué es cada columna.
 *
 * Las tareas viven dentro del acta, en la tabla que ya tiene la plantilla. No
 * se inventa una sección aparte: el acta de esta gente ya trae
 * «Id · Responsable(s) · Acuerdos / Tareas · Fecha de resolución», y duplicarla
 * habría creado dos verdades que se separan en cuanto alguien edita una.
 *
 * El problema real es que un acta suele tener VARIAS tablas que se parecen a
 * una lista de tareas. La de ejemplo tiene dos: la de acuerdos y un «Parking
 * Lot – Temas Pendientes» con columnas «Tema pendiente / Acción a tomar /
 * Responsable». Las dos hablan de pendientes y las dos tienen responsable.
 * Mandar a Planner la que no es llenaría el plan de temas que nadie se ha
 * comprometido a hacer.
 *
 * Por eso no basta con "la primera tabla que mencione tareas": se puntúan las
 * candidatas y gana la que más se parece a un compromiso —título, responsable
 * y fecha—. La de acuerdos tiene las tres; el parking lot no tiene fecha.
 *
 * Nada de esto toca React: son funciones puras para poder probarlas con datos
 * de verdad en lugar de a ojo sobre la pantalla.
 */

// El texto de las cabeceras es de quien escribió la plantilla, así que se
// reconoce por familias de palabras y no por coincidencia exacta.
const ES_TAREA = /(acuerdo|tarea|compromiso|acci[oó]n|pendiente|actividad|entregable)/i;
const ES_RESPONSABLE = /(responsable|encargad|due[ñn]o|asignad|qui[eé]n|owner)/i;
const ES_FECHA = /(fecha|vence|vencimiento|l[ií]mite|plazo|entrega|resoluci[oó]n|cu[aá]ndo|deadline)/i;
const ES_ID = /^\s*(id|no\.?|n[úu]m(?:ero)?\.?|#|consecutivo)\s*$/i;

/** El primer índice que cumpla, saltándose los ya asignados. */
function columnaQue(columnas, patron, tomadas) {
  for (let i = 0; i < columnas.length; i++) {
    if (tomadas.has(i)) continue;
    if (patron.test(String(columnas[i] || ''))) return i;
  }
  return -1;
}

/**
 * Qué significa cada columna de una tabla, si es que es de tareas.
 *
 * Devuelve `null` cuando no lo es. El orden importa: primero lo inequívoco
 * —id, responsable, fecha— y la tarea se queda con lo que reste, porque es la
 * columna de texto largo y la que peor se reconoce por su título.
 */
export function columnasDeTareas(columnas) {
  if (!Array.isArray(columnas) || columnas.length < 2) return null;

  const tomadas = new Set();
  const asignar = (patron) => {
    const i = columnaQue(columnas, patron, tomadas);
    if (i >= 0) tomadas.add(i);
    return i;
  };

  const id = asignar(ES_ID);
  const responsable = asignar(ES_RESPONSABLE);
  const fecha = asignar(ES_FECHA);
  const titulo = asignar(ES_TAREA);

  // Sin una columna que diga qué hay que hacer no hay tarea que crear. Se exige
  // que lo diga su cabecera: si se aceptara "la primera que sobre", la tabla de
  // asistentes pasaría por lista de tareas y se crearían tareas llamadas
  // "Iván Rodríguez".
  if (titulo < 0) return null;

  // Y sin responsable ni fecha es una lista de temas, no de compromisos.
  if (responsable < 0 && fecha < 0) return null;

  return {
    id: id >= 0 ? id : null,
    titulo,
    responsable: responsable >= 0 ? responsable : null,
    fecha: fecha >= 0 ? fecha : null,
    // Cuántas de las tres cosas tiene. Sirve para elegir entre varias tablas.
    fuerza: 2 + (responsable >= 0 ? 2 : 0) + (fecha >= 0 ? 1 : 0)
  };
}

/**
 * La zona del acta que son las tareas.
 *
 * @param {Array} regiones - las zonas detectadas, con `tipo` y `columnas`
 * @returns {{region: Object, columnas: Object}|null}
 */
export function zonaDeTareas(regiones) {
  let mejor = null;
  for (const zona of zonasDeTareas(regiones)) {
    // En empate gana la primera: el orden del acta es el orden en que su autor
    // las puso, y la principal suele ir antes que los anexos.
    if (!mejor || zona.columnas.fuerza > mejor.columnas.fuerza) mejor = zona;
  }
  return mejor;
}

/**
 * TODAS las tablas del acta que son de tareas, en el orden en que están.
 *
 * Un acta suele traer más de una: la de acuerdos —con responsable y fecha— y un
 * «Parking Lot / Temas pendientes» con responsable pero sin fecha. Las dos son
 * compromisos de alguien, y las dos se pueden querer en Planner.
 *
 * Durante un tiempo esto devolvía solo la más fuerte, y el parking lot no había
 * manera de mandarlo: no es que saliera mal, es que no salía. Elegir por la
 * aplicación cuál de las dos importa es justo lo que no hay que hacer — quien
 * escribió el acta puso las dos.
 *
 * Se devuelven en el orden del documento y no por fuerza, porque es el orden en
 * que la persona las va a reconocer al leer la pantalla.
 *
 * @param {Array} regiones - las zonas detectadas, con `tipo` y `columnas`
 * @returns {Array<{region: Object, columnas: Object}>}
 */
export function zonasDeTareas(regiones) {
  if (!Array.isArray(regiones)) return [];

  const zonas = [];
  for (const region of regiones) {
    if (!region || region.tipo !== 'lista') continue;
    const columnas = columnasDeTareas(region.columnas);
    if (!columnas) continue;
    zonas.push({ region, columnas });
  }
  return zonas;
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

/** El número de mes de un nombre escrito como sea: "sept", "Septiembre", "set". */
function mesPorNombre(texto) {
  const limpio = String(texto || '').toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (!limpio) return -1;
  const i = MESES.findIndex(m => {
    const base = m.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return base.startsWith(limpio.slice(0, 3)) && limpio.length >= 3;
  });
  // "set" por "septiembre" es común y no empieza igual.
  if (i < 0 && /^set/.test(limpio)) return 8;
  return i;
}

/**
 * La fecha de vencimiento que escribió una persona, como fecha de verdad.
 *
 * En un acta nadie pone la fecha en ISO: pone "7 septiembre", "19/09", "22 de
 * octubre" o lo deja en blanco. Aquí se acepta todo eso.
 *
 * El año casi nunca está escrito, y suponerlo mal manda la tarea a un año que
 * no es. Se toma el de la reunión, con una corrección: si la fecha resultante
 * cae bastante ANTES de la reunión, es que se refiere al año siguiente —un
 * compromiso adquirido en diciembre con fecha "10 de enero" no vence diez meses
 * atrás—. El margen es generoso a propósito, porque una tarea vencida hace
 * poco es normal y no hay que empujarla un año adelante.
 *
 * @param {string} texto - lo que hay escrito en la celda
 * @param {Date} referencia - la fecha de la reunión
 * @returns {{iso: string, fecha: Date}|null} `null` si no hay fecha legible
 */
export function fechaLimite(texto, referencia = new Date()) {
  const crudo = String(texto || '').trim();
  if (!crudo) return null;

  const base = referencia instanceof Date && !isNaN(referencia) ? referencia : new Date();
  let dia = null, mes = null, anio = null;

  // 2026-09-19
  let m = crudo.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    anio = Number(m[1]); mes = Number(m[2]) - 1; dia = Number(m[3]);
  }

  // 19/09/2026, 19/09, 19-9
  if (dia === null) {
    m = crudo.match(/(\d{1,2})\s*[/\-.]\s*(\d{1,2})(?:\s*[/\-.]\s*(\d{2,4}))?/);
    if (m) {
      dia = Number(m[1]); mes = Number(m[2]) - 1;
      if (m[3]) anio = Number(m[3].length === 2 ? '20' + m[3] : m[3]);
    }
  }

  // 7 septiembre, 22 de octubre de 2026, 7 sept
  if (dia === null) {
    m = crudo.match(/(\d{1,2})\s*(?:de\s+)?([A-Za-zÁÉÍÓÚáéíóúñÑ]+)\.?(?:\s*(?:de\s+)?(\d{4}))?/);
    if (m) {
      const n = mesPorNombre(m[2]);
      if (n >= 0) {
        dia = Number(m[1]); mes = n;
        if (m[3]) anio = Number(m[3]);
      }
    }
  }

  if (dia === null || mes === null || mes < 0 || mes > 11 || dia < 1 || dia > 31) return null;

  const supuesto = anio === null;
  if (supuesto) anio = base.getFullYear();

  let fecha = new Date(anio, mes, dia, 12, 0, 0);
  // Un día que no existe —31 de septiembre— desborda al mes siguiente.
  if (fecha.getMonth() !== mes) return null;

  if (supuesto) {
    const margen = new Date(base.getTime() - 120 * 24 * 3600 * 1000);
    if (fecha < margen) fecha = new Date(anio + 1, mes, dia, 12, 0, 0);
  }

  return { iso: isoDePlanner(fecha), fecha };
}

/**
 * La fecha como la quiere Planner.
 *
 * Planner guarda un instante en UTC y enseña el día que le corresponda. Puesto
 * a medianoche local, a quien esté en México (UTC-6) se le vería el día
 * anterior. Las 12:00 UTC aguantan cualquier huso sin cambiar de día.
 */
function isoDePlanner(fecha) {
  const p = (n) => String(n).padStart(2, '0');
  return `${fecha.getFullYear()}-${p(fecha.getMonth() + 1)}-${p(fecha.getDate())}T12:00:00Z`;
}

/**
 * El texto de un compromiso, reducido a lo que lo identifica.
 *
 * Es la MISMA normalización que hace el servidor al guardar el vínculo con la
 * tarea de Planner, y tiene que seguir siéndolo: aquí decide si una fila del
 * acta ya tiene tarea, y allí decide si se admite crearla. Si las dos dejaran
 * de coincidir, la pantalla enseñaría «todavía no existe» sobre algo que el
 * servidor va a rechazar por duplicado — o al revés, que es peor.
 *
 * Sin acentos, sin mayúsculas y sin espacios de más: corregir una tilde o
 * partir una línea no puede convertir un acuerdo en otro, porque entonces se
 * podría crear su tarea dos veces sin darse cuenta.
 */
export function claveDeTarea(texto) {
  return String(texto || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 400);
}

/**
 * «2026-09-18T12:00:00Z» → «2026-09-18».
 *
 * Es lo que entiende un `<input type="date">`. Se corta la cadena en vez de
 * pasar por `Date`: el instante guardado son las 12:00 UTC, y convertirlo a
 * fecha local para volver a formatearlo es dar dos oportunidades a que el día
 * se mueva por el huso horario. Lo que hay escrito ya es el día bueno.
 */
export function diaDeISO(iso) {
  return typeof iso === 'string' && iso.length >= 10 ? iso.slice(0, 10) : '';
}

/** «2026-09-18» → el instante que Planner guarda para ese día. */
export function isoDeDia(dia) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(dia || '')) ? `${dia}T12:00:00Z` : null;
}

/**
 * Las tareas de una tabla del acta, listas para revisar y mandar a Planner.
 *
 * Se conserva `fila` —el índice en la tabla— para poder devolver a su sitio lo
 * que se corrija: quien edite el responsable aquí lo está editando en el acta,
 * no en una copia.
 *
 * @param {Array<Array<string>>} filas - la tabla tal cual está en el acta
 * @param {Object} columnas - lo que devuelve `columnasDeTareas`
 * @param {Date} referencia - la fecha de la reunión, para completar los años
 */
export function tareasDeZona(filas, columnas, referencia = new Date(), zonaId = '') {
  if (!Array.isArray(filas) || !columnas) return [];

  return filas.map((fila, i) => {
    const celda = (n) => (n === null || !Array.isArray(fila) ? '' : String(fila[n] || '').trim());
    const titulo = celda(columnas.titulo);
    const fechaTexto = celda(columnas.fecha);
    const limite = fechaTexto ? fechaLimite(fechaTexto, referencia) : null;

    return {
      fila: i,
      // De qué tabla salió y cuál es su casilla única en la pantalla.
      //
      // La pantalla guarda el responsable, la fecha y el visto de cada tarea en
      // objetos indexados por esta clave. Cuando se manda más de una tabla, el
      // número de fila solo no vale: la fila 0 de Acuerdos y la fila 0 de
      // Parking Lot serían la misma casilla, y elegir persona en una cambiaría
      // la otra sin que se vea.
      zona: zonaId,
      clave: `${zonaId}:${i}`,
      id: celda(columnas.id),
      titulo,
      responsable: celda(columnas.responsable),
      fechaTexto,
      vence: limite ? limite.iso : null,
      // Una fila sin texto no es una tarea: es una fila de relleno de la
      // plantilla, y crearla en Planner sería crear una tarea sin nombre.
      vacia: titulo === '',
      // La fecha estaba escrita pero no hay manera de entenderla. No es lo
      // mismo que no tener fecha, y conviene decirlo antes de crear la tarea.
      fechaIlegible: Boolean(fechaTexto) && !limite
    };
  });
}

const tareasDeLaMinuta = {
  columnasDeTareas, zonaDeTareas, zonasDeTareas, tareasDeZona, fechaLimite,
  diaDeISO, isoDeDia, claveDeTarea
};
export default tareasDeLaMinuta;
